import { useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { Search, Building2 } from 'lucide-react'
import { useAvailableSuppliersForGroup } from '@/hooks/useAdSupplierGroups'
import { adSupplierGroupService } from '@/services/ad_supplier_group_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { useDebounce } from '@/hooks/useDebounce'

interface AssignAdSupplierDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId: number
  onSuccess: () => void
}

export function AssignAdSupplierDialog({
  open,
  onOpenChange,
  groupId,
  onSuccess,
}: AssignAdSupplierDialogProps) {
  const { token } = useAuth()
  const [search, setSearch] = useState('')
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<number[]>([])
  const [isAssigning, setIsAssigning] = useState(false)

  const debouncedSearch = useDebounce(search, 300)

  const { adSuppliers, isLoading } = useAvailableSuppliersForGroup(open ? groupId : null, {
    search: debouncedSearch || undefined,
    pageSize: 50,
  })

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSearch('')
      setSelectedSupplierIds([])
    }
  }, [open])

  const handleSelectSupplier = (supplierId: number, checked: boolean) => {
    if (checked) {
      setSelectedSupplierIds((prev) => [...prev, supplierId])
    } else {
      setSelectedSupplierIds((prev) => prev.filter((id) => id !== supplierId))
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked && adSuppliers) {
      setSelectedSupplierIds(adSuppliers.map((s) => s.supplier_id))
    } else {
      setSelectedSupplierIds([])
    }
  }

  const isAllSelected = useMemo(() => {
    return adSuppliers && adSuppliers.length > 0 && selectedSupplierIds.length === adSuppliers.length
  }, [adSuppliers, selectedSupplierIds])

  const handleAssign = async () => {
    if (!token || selectedSupplierIds.length === 0) return

    setIsAssigning(true)
    let successCount = 0
    let failCount = 0

    for (const supplierId of selectedSupplierIds) {
      const result = await adSupplierGroupService.assignSupplier(groupId, supplierId, token)
      if (result.success) {
        successCount++
      } else {
        failCount++
      }
    }

    setIsAssigning(false)

    if (successCount > 0) {
      toast.success(`成功指派 ${successCount} 間投資方`)
      onSuccess()
      onOpenChange(false)
    }

    if (failCount > 0) {
      toast.error(`${failCount} 間投資方指派失敗`)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>指派投資方到集團</DialogTitle>
          <DialogDescription>選擇要指派到此集團的投資方（可多選）</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="搜尋投資方名稱..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Supplier list */}
          <div className="border rounded-md">
            {/* Header with select all */}
            {adSuppliers && adSuppliers.length > 0 && (
              <div className="flex items-center gap-3 p-3 border-b bg-muted/50">
                <Checkbox
                  id="select-all"
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                />
                <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                  全選
                </label>
                <span className="text-sm text-muted-foreground ml-auto">
                  可指派的投資方（尚未歸屬其他集團）
                </span>
              </div>
            )}

            <ScrollArea className="h-[300px]">
              {isLoading ? (
                <div className="p-4 space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="h-4 w-4" />
                      <Skeleton className="h-4 flex-1" />
                    </div>
                  ))}
                </div>
              ) : adSuppliers && adSuppliers.length > 0 ? (
                <div className="divide-y">
                  {adSuppliers.map((supplier) => (
                    <div
                      key={supplier.supplier_id}
                      className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer"
                      onClick={() =>
                        handleSelectSupplier(
                          supplier.supplier_id,
                          !selectedSupplierIds.includes(supplier.supplier_id),
                        )
                      }
                    >
                      <Checkbox
                        checked={selectedSupplierIds.includes(supplier.supplier_id)}
                        onCheckedChange={(checked) =>
                          handleSelectSupplier(supplier.supplier_id, checked as boolean)
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{supplier.supplier_name}</div>
                        {supplier.manager && (
                          <div className="text-sm text-muted-foreground truncate">
                            負責人: {supplier.manager}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full py-8 text-muted-foreground">
                  <Building2 className="h-8 w-8 mb-2" />
                  <p>沒有可指派的投資方</p>
                  <p className="text-sm">所有投資方都已歸屬集團或不符合搜尋條件</p>
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Selected count */}
          <div className="text-sm text-muted-foreground">
            已選擇{' '}
            <span className="font-medium text-foreground">{selectedSupplierIds.length}</span>{' '}
            間投資方
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            type="button"
            onClick={handleAssign}
            disabled={selectedSupplierIds.length === 0 || isAssigning}
          >
            {isAssigning ? '指派中...' : `確定指派 (${selectedSupplierIds.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
